// Typed client for the BFF at /api/* (PostgREST semantics). Every call goes
// through the Cloudflare Pages Function, which injects the service_role key.

import type { Insert, Row, Update } from "@/types/database";

type Table =
  | "artists"
  | "projects"
  | "tracks"
  | "track_tags"
  | "track_concepts"
  | "track_prompts"
  | "track_generations"
  | "track_assets"
  | "track_master"
  | "track_release";

export class ApiError extends Error {
  status: number;
  detail?: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      (data && (data.message || data.error || data.hint)) ||
      `Request failed (${res.status})`;
    throw new ApiError(res.status, message, data);
  }
  return data as T;
}

export interface ListResult<T> {
  rows: T[];
  total: number;
}

/** GET a collection. `query` is a raw PostgREST query string (without `?`). */
export async function list<T extends Table>(
  table: T,
  query = "",
): Promise<ListResult<Row<T>>> {
  const res = await fetch(`/api/${table}${query ? `?${query}` : ""}`, {
    headers: { Accept: "application/json" },
  });
  const rows = await parse<Row<T>[]>(res);
  // Content-Range: e.g. "0-24/111" — the part after the slash is the total.
  const range = res.headers.get("Content-Range");
  const total = range ? Number(range.split("/")[1]) : rows.length;
  return { rows, total: Number.isFinite(total) ? total : rows.length };
}

/** GET a single row by primary key. Returns null if not found. */
export async function getOne<T extends Table>(
  table: T,
  id: number,
  query = "",
): Promise<Row<T> | null> {
  const q = [`id=eq.${id}`, query].filter(Boolean).join("&");
  const { rows } = await list(table, q);
  return rows[0] ?? null;
}

/** INSERT one row, returning the created record. */
export async function create<T extends Table>(
  table: T,
  body: Insert<T>,
): Promise<Row<T>> {
  const res = await fetch(`/api/${table}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Prefer: "return=representation" },
    body: JSON.stringify(body),
  });
  const rows = await parse<Row<T>[]>(res);
  return rows[0];
}

/** UPDATE a row by primary key, returning the updated record. */
export async function update<T extends Table>(
  table: T,
  id: number,
  body: Update<T>,
): Promise<Row<T>> {
  const res = await fetch(`/api/${table}?id=eq.${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Prefer: "return=representation" },
    body: JSON.stringify(body),
  });
  const rows = await parse<Row<T>[]>(res);
  return rows[0];
}

/**
 * UPSERT rows on a conflict target (PostgREST `on_conflict`). Used for 1:1
 * detail tables keyed on track_id (concept/master/release).
 */
export async function upsert<T extends Table>(
  table: T,
  onConflict: string,
  body: Insert<T> | Insert<T>[],
): Promise<Row<T>[]> {
  const res = await fetch(`/api/${table}?on_conflict=${onConflict}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Prefer: "return=representation,resolution=merge-duplicates",
    },
    body: JSON.stringify(body),
  });
  return parse<Row<T>[]>(res);
}

/** DELETE a row by primary key. */
export async function remove<T extends Table>(table: T, id: number): Promise<void> {
  const res = await fetch(`/api/${table}?id=eq.${id}`, {
    method: "DELETE",
    headers: { Prefer: "return=minimal" },
  });
  if (!res.ok) await parse(res);
}
